/**
 * The duplicate finder's key contract — the frontend mirror of the backend's
 * `DuplicateKeys.ALLOWED` (main-backend, org.nikita.app.guest.DuplicateKeys).
 *
 * <p>`GET /guests/{eventId}/duplicates?field=a,b,…` groups the active guests
 * of an event by a COMPOSITE key: a guest is a duplicate when ALL listed keys
 * equal another guest's values (AND semantics). The backend accepts only the
 * 28 scalar stored fields below — no ids, dates, arrays, sub-documents or
 * computed joins — and refuses anything else with a 400 the guest list has no
 * good place to show. So the picker offers exactly this list, and the two
 * lists are pinned identical: `duplicateKeys.test.ts` on this side (every
 * entry is a real built-in guest field with a label), the e2e G9e loop on
 * the wire (every entry is accepted).
 *
 * <p>Keep the order human: it is the order the backend lists them in its AI
 * schema, not the sort order — {@link canonicalDuplicateKeys} sorts.
 */
export const DUPLICATE_KEY_FIELDS: readonly string[] = [
  "firstName",
  "lastName",
  "company",
  "jobTitle",
  "title",
  "gender",
  "email",
  "phone",
  "mobile",
  "address",
  "address2",
  "zipCode",
  "city",
  "country",
  "language",
  "username",
  "code",
  "guestType",
  "state",
  "emailOptIn",
  "checkedIn",
  "numCompanions",
  "origin",
  "importId",
  "speakerId",
  "sponsorId",
  "emailUndeliverable",
  "lastEmailStatus",
] as const;

const DUPLICATE_KEY_SET: ReadonlySet<string> = new Set(DUPLICATE_KEY_FIELDS);

/** Backend cap on keys per request (DuplicateKeys.MAX_KEYS). */
export const MAX_DUPLICATE_KEYS = 5;

/**
 * Keys an event VIEWER may not read (BG18) — and therefore may not group by:
 * the backend answers 403 `duplicateFieldForbidden`. Mirror of the backend's
 * `VIEWER_HIDDEN_GUEST_FIELDS` restricted to the keys the picker could offer.
 */
export const VIEWER_HIDDEN_DUPLICATE_KEYS: readonly string[] = ["code", "username"] as const;

export function isDuplicateKeyField(name: string): boolean {
  return DUPLICATE_KEY_SET.has(name);
}

/**
 * The canonical form the backend produces from the raw `field` value: each
 * key once, sorted. `["lastName","email"]` and `["email","lastName"]` are the
 * SAME request — the provider's request signature and the backend's `$group`
 * key both rely on that, so it is applied at the provider boundary, not only
 * in the dialog.
 */
export function canonicalDuplicateKeys(keys: Iterable<string>): string[] {
  return Array.from(new Set(keys)).sort();
}
